// Contract files in the tenant storage (spec 013 / Contracts 020): same path as every other PreBiller file.
//  1. ask Contracts for an upload SAS (ProtocolContract, entity Contract)
//  2. PUT the binary straight to Azure with the SAS URL (the binary never goes through the API)
//  3. register the blob in the contract right away (the storage event may arrive later or never)
import { AzureFileEntityTypeEnum, AzureFileTypeEnum } from '../models/Enums/AzureFileTypeEnum';
import { FileUpload, FileUploadRequest } from '../models/Entities/Contracts/FileUpload';
import { HttpResult } from '../models/HttpResult';
import { buildBlobFileName, getExtension } from '../functions/AzureFileUpload.Functions';
import { toTenantRelativePath } from '../functions/StorageManagement.Functions';
import { requestUploadSas, uploadToAzure } from './AzureFileUpload.Services';
import { apiRequest } from './commons/CommonService.Functions';

const Gateway = process.env.REACT_APP_BILLER_API_GATEWAY;

/** POST contracts/{contractId}/files/register → Response<ContractFileViewModel> (idempotent by blob path). */
export const RegisterContractFileURL = (contractId: number): string => `${Gateway}contracts/${contractId}/files/register`;

export const registerContractFile = async (token: string, contractId: number, path: string): Promise<FileUpload> =>
  apiRequest<FileUpload>(RegisterContractFileURL(contractId), token, 'RegisterContractFile', {
    method: 'POST',
    body: JSON.stringify({ path }),
  });

/**
 * Uploads a contract file to the tenant storage and registers it in the contract. Rejects with an ErrorType
 * (SAS, Azure or register failure); a failed register leaves the blob uploaded and the storage event can still register it.
 */
export const uploadContractFileToStorage = async (
  request: FileUploadRequest & { entityId: number },
  token: string
): Promise<HttpResult<FileUpload>> => {
  const { file, entityId, onUploadProgress } = request;

  const sas = await requestUploadSas(token, {
    fileType: AzureFileTypeEnum.ProtocolContract,
    entityType: AzureFileEntityTypeEnum.Contract,
    entityId,
    // name + "_" + timestamp: two uploads with the same name never overwrite each other
    fileName: buildBlobFileName(file.name),
    fileExtension: getExtension(file.name) || 'bin',
  });

  await uploadToAzure(sas.sasUrl, file, onUploadProgress);

  const registered = await registerContractFile(token, entityId, toTenantRelativePath(sas.blobPath));

  return {
    data: registered,
    succeeded: true,
    message: 'File uploaded successfully',
    statusCode: 200,
    errors: [],
  } as HttpResult<FileUpload>;
};

/** Contract documents go to the storage; any other entity keeps the legacy multipart upload. */
export const isContractEntity = (entityType: string | undefined): boolean => (entityType ?? '').trim().toLowerCase() === 'contract';
